/**
 * Plan name mapping utility
 * Maps database plan names to frontend display names
 */

export const PLAN_DISPLAY_NAMES = {
  'Grow': 'Grow',
  'Starter': 'Optimize',
  'Pro': 'Scale',
  'Basic': 'Grow',
  'Premium': 'Optimize',
  'Enterprise': 'Scale',
  // Fallback to slug-based mapping
  'grow': 'Grow',
  'starter': 'Optimize',
  'pro': 'Scale',
  'basic': 'Grow',
  'premium': 'Optimize',
  'enterprise': 'Scale'
};

/**
 * Reduce a plan name or slug to the key the table is written in.
 *
 * The table was matched EXACTLY, which is why "Moves to Pro Plan at period end"
 * reached a user: the API sends the pending plan as "Pro Plan", the table is
 * keyed on "Pro", the lookup missed, and the raw database name fell through to
 * the screen — naming a plan this product does not sell. The current-plan chip
 * escaped it only because that payload also carries a slug to fall back on.
 *
 * So: case-insensitive, and the trailing word "Plan" — which is decoration, not
 * part of the name — is dropped before matching.
 */
const canonicalPlanKey = (value) => String(value ?? '')
  .trim()
  .replace(/\s+plan$/i, '')
  .toLowerCase();

// The same table, keyed canonically, so one lookup handles "Pro", "pro",
// "Pro Plan" and "PRO PLAN" alike.
const DISPLAY_BY_KEY = Object.entries(PLAN_DISPLAY_NAMES).reduce((acc, [key, display]) => {
  acc[canonicalPlanKey(key)] = display;
  return acc;
}, {});

/**
 * Get the frontend display name for a plan
 * @param {Object} plan - Plan object from API
 * @returns {string} Display name for frontend
 */
export const getPlanDisplayName = (plan) => {
  if (!plan) return '';

  // ── The free tier is the Basic plan ───────────────────────────────────────
  // Checked BEFORE the name table, and this order is the whole point. The
  // PAID entry tier answers to the slug 'basic' and is displayed as "Grow" —
  // so once the free plan is renamed to "Basic" in the database, matching on
  // its name would send it straight to the 'Basic' -> 'Grow' row above and
  // label a free account as the cheapest PAID plan.
  //
  // The slug is what identifies this tier everywhere else that matters
  // (deriveFreeTierState, websiteLimitCopy, PlanSelector's filter all read
  // slug === 'free'), so it is what is read here too.
  if (canonicalPlanKey(plan.slug) === 'free') return 'Basic';

  // Try to map by name first, then by slug.
  const byName = DISPLAY_BY_KEY[canonicalPlanKey(plan.name)];
  if (byName) return byName;

  const bySlug = DISPLAY_BY_KEY[canonicalPlanKey(plan.slug)];
  if (bySlug) return bySlug;

  // Ultimate fallback: return original name
  return plan.name || plan.slug || 'Unknown Plan';
};

/**
 * Are these two plan names the same plan?
 *
 * Compared canonically because the API is not consistent about the "Plan"
 * suffix between the current plan and the pending one — "Pro" and "Pro Plan"
 * are the same plan, and a raw string compare says otherwise.
 */
export const isSamePlanName = (a, b) => {
  const left = canonicalPlanKey(a);
  const right = canonicalPlanKey(b);
  return Boolean(left) && left === right;
};

/**
 * The paid tiers, cheapest first, BY DISPLAY NAME.
 *
 * ── WHY DISPLAY NAMES AND NOT SLUGS ─────────────────────────────────────────
 * Every tier answers to several slugs — Grow is 'basic' or 'grow', Optimize is
 * 'starter' or 'premium', Scale is 'pro' or 'enterprise' — and PLAN_DISPLAY_NAMES
 * above already collapses all of them onto one name. A ladder keyed on slugs
 * would be a second table with the same job and its own set of aliases to keep
 * in step; keyed on the name it comes out of, there is only one.
 *
 * The free tier is absent on purpose: this answers "what is there to move UP to",
 * and nobody upgrades to Free.
 */
export const PAID_TIER_ORDER = ['Grow', 'Optimize', 'Scale'];

/**
 * The tiers above a given one — 'Grow' → ['Optimize', 'Scale'].
 *
 * For copy that has to name what an upgrade actually offers, rather than saying
 * "upgrade your plan" and leaving the reader to find out where.
 *
 * Empty for the top tier, and for any name not on the ladder — Free included,
 * which never asks this question (it is told to "sign up for a paid plan", not
 * pointed at a specific one). An empty list means the caller drops the clause
 * rather than printing a dangling "upgrade to the  plan."
 */
export const higherPaidTiers = (displayName) => {
  const name = String(displayName ?? '').trim();
  if (!name) return [];
  const i = PAID_TIER_ORDER.findIndex((t) => t.toLowerCase() === name.toLowerCase());
  // Not a paid tier (Free, or an unrecognised name): everything paid is above it.
  if (i === -1) return [];
  return PAID_TIER_ORDER.slice(i + 1);
};

/**
 * Get plan description based on slug or name
 * @param {Object} plan - Plan object from API
 * @returns {string} Plan description
 */
export const getPlanDescription = (plan) => {
  const slug = plan.slug || plan.name?.toLowerCase();
  
  const descriptions = {
    'basic': "Best if you're growth-focused and need scalability",
    'starter': "Monthly subscription plan for active creators",
    'pro': "The ultimate package for digital editorial teams",
    'premium': "Monthly subscription plan for active creators",
    'enterprise': "The ultimate package for digital editorial teams"
  };
  
  return descriptions[slug] || "Monthly subscription plan";
};

/**
 * Check if plan is marked as popular
 * @param {Object} plan - Plan object from API
 * @returns {boolean} Whether plan should show "Popular" badge
 */
export const isPlanPopular = (plan) => {
  const slug = plan.slug || plan.name?.toLowerCase();
  // Mark "Optimize" (starter/premium) as popular
  return slug === 'starter' || slug === 'premium';
};

/**
 * How a plan card relates to the plan the user is actually on.
 *
 * This is the single input that drives every plan-button colour and label, so
 * the four scenarios in the spec fall out of one rule instead of being
 * hand-styled per card:
 *
 *   free / trial → every paid plan is an UPGRADE
 *   on Grow      → Grow is CURRENT, Optimize and Scale are UPGRADEs
 *   on Optimize  → Grow is a DOWNGRADE, Optimize CURRENT, Scale an UPGRADE
 *   on Scale     → Grow and Optimize are DOWNGRADEs, Scale is CURRENT
 */
export const PLAN_RELATIONSHIP = {
  CURRENT: 'current',
  UPGRADE: 'upgrade',
  DOWNGRADE: 'downgrade',
};

/**
 * @param {Object} plan         the plan being rendered
 * @param {Object|null} currentPlan  the plan the user is on, or null on free/trial
 * @returns {'current'|'upgrade'|'downgrade'}
 */
export const getPlanRelationship = (plan, currentPlan) => {
  if (!plan) return PLAN_RELATIONSHIP.UPGRADE;

  // `is_current` comes from the server; the id compare is a local safety net so
  // a missing/stale flag can't render two cards as "Current Plan".
  if (plan.is_current === true || (currentPlan && plan.id === currentPlan.id)) {
    return PLAN_RELATIONSHIP.CURRENT;
  }

  // No paid subscription (free plan or trial) — scenario 1. Everything is an
  // upgrade, and nothing should ever read as a downgrade.
  if (!currentPlan) return PLAN_RELATIONSHIP.UPGRADE;

  // Price decides direction. Derived locally rather than trusting the server's
  // can_upgrade/can_downgrade booleans, so the styling stays correct even if
  // those are absent from an older response. They are the fallback, not the
  // source.
  const planPrice = Number(plan.price_cents);
  const currentPrice = Number(currentPlan.price_cents);
  if (Number.isFinite(planPrice) && Number.isFinite(currentPrice)) {
    return planPrice < currentPrice
      ? PLAN_RELATIONSHIP.DOWNGRADE
      : PLAN_RELATIONSHIP.UPGRADE;
  }

  if (plan.can_downgrade === true) return PLAN_RELATIONSHIP.DOWNGRADE;
  return PLAN_RELATIONSHIP.UPGRADE;
};

const planUtils = {
  PLAN_DISPLAY_NAMES,
  getPlanDisplayName,
  getPlanDescription,
  isPlanPopular,
  PAID_TIER_ORDER,
  higherPaidTiers,
  PLAN_RELATIONSHIP,
  getPlanRelationship
};

export default planUtils;
